"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Search, ShieldCheck, Sparkles, TrendingUp } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
export default function HeroBanner() {
  const valuePoints = [{
    id: 1,
    text: "Direct Consultant Access"
  }, {
    id: 2,
    text: "Tailored SEO Strategy"
  }, {
    id: 3,
    text: "Zero Agency Overhead"
  }, {
    id: 4,
    text: "Transparent Reporting"
  }];
  return <section id="herobanner" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 lg:py-28">
      {/* Subtle background decorative shapes */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full bg-secondary/60 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          {/* Main Hero Copy */}
          <div className="flex flex-col items-start lg:col-span-7">
            <Badge variant="secondary" className="mb-6 gap-2 rounded-full border border-accent/30 bg-secondary/80 px-4 py-1.5 text-xs font-semibold text-foreground">
              <Sparkles className="size-3.5 text-accent" />
              <span>Independent SEO Consultant &bull; You work directly with me</span>
            </Badge>

            <h1 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              SEO Freelancer Dubai
            </h1>

            <p className="mt-6 max-w-2xl font-sans text-lg leading-relaxed text-muted-foreground sm:text-xl">
              I&apos;m Yassine Essaadi, an independent SEO freelancer helping Dubai and UAE businesses improve organic visibility, attract qualified traffic, and generate measurable leads without agency overhead. I work directly with local service businesses, e-commerce stores, and B2B companies.
            </p>

            {/* CTAs */}
            <div className="mt-8 flex w-full flex-col gap-4 sm:w-auto sm:flex-row sm:items-center">
              <Button asChild size="lg" className="h-12 rounded-md bg-primary px-7 text-base font-semibold text-primary-foreground shadow-md transition-[background-color,box-shadow] hover:bg-primary/90 hover:shadow-lg">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="h-12 rounded-md border-border bg-background px-7 text-base font-semibold text-foreground shadow-xs transition-[background-color,border-color] hover:bg-secondary hover:text-foreground">
                <Link to="/SeoAuditsPage" className="inline-flex items-center gap-2">
                  <Search className="size-4 text-accent" />
                  <span>Get a Free SEO Audit</span>
                </Link>
              </Button>
            </div>

            {/* Core Value Points */}
            <div className="mt-10 grid w-full grid-cols-2 gap-3 border-t border-border pt-8 sm:grid-cols-2 md:grid-cols-4">
              {valuePoints.map((item, i) => <div key={item.id} data-index={i} className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <CheckCircle2 className="size-4 shrink-0 text-primary" />
                  <span>{item.text}</span>
                </div>)}
            </div>
          </div>

          {/* Hero Visual Card / Profile Showcase */}
          <div className="lg:col-span-5">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              {/* Outer decorative card */}
              <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-3 shadow-xl">
                <AspectRatio ratio={4 / 5} className="overflow-hidden rounded-xl bg-muted">
                  <img src="https://mighty-wolf.10web.cloud/wp-content/uploads/2026/08/SEO-Consultant-Freelancer.png" alt="Yassine Essaadi - Independent SEO Consultant" loading="lazy" decoding="async" className="h-full w-full object-cover object-top" />
                </AspectRatio>

                {/* Floating Metric Pill: Direct Delivery */}
                <div className="absolute top-6 -left-4 hidden rounded-lg border border-border bg-background/95 p-3 shadow-lg backdrop-blur-xs sm:flex sm:items-center sm:gap-3">
                  <div className="flex size-10 items-center justify-center rounded-md bg-secondary text-primary">
                    <ShieldCheck className="size-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-foreground">100% Direct Delivery</p>
                    <p className="text-2xs text-muted-foreground">Every audit &amp; strategy is built by me</p>
                  </div>
                </div>

                {/* Floating Metric Pill: Sustainable Growth */}
                <div className="absolute -right-3 -bottom-3 rounded-lg border border-border bg-background/95 p-3 shadow-lg backdrop-blur-xs">
                  <div className="flex items-center gap-3">
                    <div className="flex size-10 items-center justify-center rounded-md bg-accent text-accent-foreground">
                      <TrendingUp className="size-5" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground">Sustainable Growth</p>
                      <p className="text-2xs text-muted-foreground">Technical, On-Page &amp; AI Search</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}